import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import last from "lodash/last";
import { ComponentProps } from "PFApp/components/activity_form/field_factory/component_prop_types";
import { propertyTypeToComponentMap as commonPropertyTypeToComponentMap } from "PFApp/components/activity_form/field_factory/property_type_to_component_map";
import { AssigneeSelector } from "PFApp/components/activity_form/form_selectors/assignee_selector";
import { EngagementSelector } from "PFApp/components/activity_form/form_selectors/engagement_selector";
import { ExpiryDateSelector } from "PFApp/components/activity_form/form_selectors/expiry_date_selector";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AVAILABILITY_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { useWatchIsNew } from "PFApp/hooks/activity_form/multi_roles_form/use_watch_is_new";
import {
  FormSchemaField,
  MultiActivityForm,
  RoleFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { useCellInputFocus } from "PFComponents/table";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { Subtemplate } from "PFCore/types";
import { Activity, Template } from "PFTypes";
import { Option } from "PFTypes/options";
import { ComponentType, RefObject, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "../../tabular_roles_form.module.scss";
import { TABULAR_FIELD_DROPDOWN_MIN_WIDTH } from "../../tabular_roles_form.utils";
import { AttachmentsCell } from "./field_cells/attachments_cell/attachments_cell";
import { AvailabilityThresholdCell } from "./field_cells/availability_threshold_cell";
import { BookingCategoryCell } from "./field_cells/booking_category_cell";
import { ChatCell } from "./field_cells/chat_cell/chat_cell";
import { CheckboxesCell } from "./field_cells/checkboxes_cell/checkboxes_cell";
import { CustomFieldCell, CustomFieldCellProps } from "./field_cells/custom_field_cell";
import { ExpiryDateCell } from "./field_cells/expiry_date_cell";
import { InterestQuestionsCell } from "./field_cells/interest_questions_cell/interest_questions_cell";
import { PrivacyCell } from "./field_cells/privacy_cell/privacy_cell";
import { SkillsFrameworksCell } from "./field_cells/skills_frameworks_cell/skills_frameworks_cell";

const propertyTypeToComponentMap = {
  ...commonPropertyTypeToComponentMap,
  checkboxes: CheckboxesCell,
  custom_field: CustomFieldCell
};

const INPUT_STATIC_FIELDS = [FIELD_NAME_KEY, FIELD_DESCRIPTION_KEY];
const NUMBER_STATIC_FIELDS = [FIELD_MIN_REQUIRED_COUNT_KEY];
const DIRECT_FOCUS_FIELD_TYPES = [
  "date",
  "date-time",
  "date-range",
  "select",
  "toggle",
  "checkboxes",
  "custom_field"
];

type TabularFieldFactoryProps = {
  field: FormSchemaField;
  value: any;
  subtemplate: Subtemplate | null;
  portalRef: RefObject<HTMLDivElement | null>;
  title: string;
  roleFormKey: RoleFormKey;
  disabled?: boolean;
  error?: string;
  onChange: (value: any) => void;
  rowIndex: number;
  colIndex: number;
  roleUniqueId: string;
  isAudit?: boolean;
  required?: boolean;
  roleTemplate: Template;
  isManageRolesView?: boolean;
};

export const TabularFieldFactory = ({
  field,
  value,
  subtemplate,
  portalRef,
  title,
  roleFormKey,
  disabled,
  error,
  onChange,
  rowIndex,
  colIndex,
  roleUniqueId,
  isAudit = false,
  required = false,
  roleTemplate,
  isManageRolesView
}: TabularFieldFactoryProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { setValue } = useFormContext();
  const roleName = useWatch<MultiActivityForm, `${RoleFormKey}.${typeof FIELD_NAME_KEY}`>({
    name: `${roleFormKey}.${FIELD_NAME_KEY}`
  });
  const isNewRole = useWatchIsNew(roleFormKey);

  const inputRef = useRef<HTMLElement | null>(null);
  const { inputFocusProps: interactiveFocusProps } = useCellInputFocus(inputRef, { rowIndex, colIndex });
  const { inputFocusProps: directClickFocusProps } = useCellInputFocus(
    inputRef,
    { rowIndex, colIndex },
    { directClick: true }
  );

  const formattedFieldTitle = t("roleFieldTitle", {
    roleName: roleName || t("emptyRoleName"),
    fieldName: title
  });

  if (INPUT_STATIC_FIELDS.includes(field.name)) {
    return (
      <InputFieldSet
        ref={inputRef as RefObject<HTMLInputElement | HTMLTextAreaElement>}
        inputType="textarea"
        maxLength={field.name === FIELD_NAME_KEY ? 100 : undefined}
        autoSize={false}
        variant="table"
        value={value}
        disabled={disabled}
        error={error}
        title={formattedFieldTitle}
        aria-label={formattedFieldTitle}
        onChange={onChange}
        {...interactiveFocusProps}
      />
    );
  }

  if (NUMBER_STATIC_FIELDS.includes(field.name)) {
    return (
      <InputFieldSet
        ref={inputRef as RefObject<HTMLInputElement | HTMLTextAreaElement>}
        inputType="number"
        variant="table"
        value={value}
        disabled={disabled}
        error={error}
        inputClassName={css.singleLineInput}
        title={formattedFieldTitle}
        aria-label={formattedFieldTitle}
        onChange={onChange}
        {...interactiveFocusProps}
      />
    );
  }

  const availabilityFieldName = last(FIELD_AVAILABILITY_KEY.split("."))!;

  switch (field.name) {
    case FIELD_ASSIGNEE_ID_KEY:
      return (
        <AssigneeSelector
          ref={inputRef as RefObject<HTMLInputElement>}
          value={value}
          onChange={onChange}
          portalRef={portalRef}
          title={formattedFieldTitle}
          variant="table"
          disabled={disabled}
          error={error}
          aria-label={formattedFieldTitle}
          focusSearchInputOnOpen={false}
          dropdownId={`role-${roleUniqueId}-${field.name}`}
          {...directClickFocusProps}
        />
      );
    case FIELD_ATTACHMENT_IDS_KEY:
      return (
        <AttachmentsCell
          buttonRef={inputRef as RefObject<HTMLButtonElement>}
          onChange={onChange}
          roleFormKey={roleFormKey}
          disabled={disabled}
          error={error}
          inputFocusProps={directClickFocusProps}
        />
      );
    case availabilityFieldName:
      return (
        <AvailabilityThresholdCell
          ref={inputRef as RefObject<HTMLInputElement>}
          availability={value}
          disabled={disabled}
          onChange={onChange}
          error={error}
          inputFocusProps={interactiveFocusProps}
        />
      );
    case FIELD_BOOKING_CATEGORY_ID_KEY:
      return (
        <BookingCategoryCell
          title={formattedFieldTitle}
          value={value}
          onChange={onChange}
          subtemplate={subtemplate}
          roleFormKey={roleFormKey}
          portalRef={portalRef}
          disabled={disabled}
          error={error}
          inputFocusProps={directClickFocusProps}
        />
      );
    case FIELD_COMMUNICATION_TYPE_KEY:
      return (
        <ChatCell
          buttonRef={inputRef as RefObject<HTMLButtonElement>}
          title={formattedFieldTitle}
          conversationType={value}
          roleFormKey={roleFormKey}
          disabled={disabled}
          onChange={onChange}
          error={error}
          inputFocusProps={directClickFocusProps}
        />
      );
    case FIELD_INTEREST_QUESTIONS_KEY:
      return (
        <InterestQuestionsCell
          buttonRef={inputRef as RefObject<HTMLButtonElement>}
          questions={value}
          disabled={disabled}
          onChange={onChange}
          roleFormKey={roleFormKey}
          error={error}
          inputFocusProps={directClickFocusProps}
        />
      );
    case FIELD_POST_UNTIL_KEY: {
      const hasChangeReasons = !isEmpty(roleTemplate?.localized_post_until_change_reasons);

      if (!isNewRole && hasChangeReasons) {
        return (
          <ExpiryDateCell
            buttonRef={inputRef as RefObject<HTMLButtonElement>}
            value={value}
            roleFormKey={roleFormKey}
            onChange={onChange}
            title={formattedFieldTitle}
            disabled={disabled}
            error={error}
            inputFocusProps={directClickFocusProps}
          />
        );
      }

      return (
        <ExpiryDateSelector
          inputRef={inputRef as RefObject<HTMLInputElement>}
          className={css.singleLineInput}
          onChange={onChange}
          value={value}
          portalRef={portalRef}
          variant="table"
          disabled={disabled}
          error={error}
          {...directClickFocusProps}
        />
      );
    }
    case FIELD_PRIVACY_KEY:
      return (
        <PrivacyCell
          buttonRef={inputRef as RefObject<HTMLButtonElement>}
          title={formattedFieldTitle}
          isPrivate={value}
          roleFormKey={roleFormKey}
          disabled={disabled}
          onChange={onChange}
          error={error}
          inputFocusProps={directClickFocusProps}
          isDeferApply={isManageRolesView}
        />
      );
    case FIELD_SKILLS_FRAMEWORK_IDS_KEY:
      return (
        <SkillsFrameworksCell
          buttonRef={inputRef as RefObject<HTMLButtonElement>}
          roleFormKey={roleFormKey}
          subtemplate={subtemplate}
          value={value}
          disabled={disabled}
          onChange={onChange}
          error={error}
          inputFocusProps={directClickFocusProps}
        />
      );
    case FIELD_PARENT_ACTIVITY_KEY: {
      const handleEngagementChange = (option: Option<Activity>) => {
        const engagement = option.original;
        const { isForceVisibilitySet } = getPrivacyConfiguration({ template: roleTemplate });

        if (
          engagement &&
          !isForceVisibilitySet &&
          roleTemplate.default_visibility === "inherit_from_parent"
        ) {
          setValue(`${roleFormKey}.${FIELD_PRIVACY_KEY}` as any, engagement.private, { shouldDirty: true });
        }

        onChange(engagement);
      };

      return (
        <EngagementSelector
          id={`role-${roleUniqueId}-${FIELD_PARENT_ACTIVITY_KEY}`}
          isAudit={isAudit}
          value={value?.id}
          onChange={handleEngagementChange}
          onClear={!isNil(value) && !required ? () => onChange(null) : undefined}
          portalRef={portalRef}
          title={formattedFieldTitle}
          variant="table"
          disabled={disabled}
          error={error}
          required={required}
          focusSearchInputOnOpen={false}
          dropdownId={`role-${roleUniqueId}-${FIELD_PARENT_ACTIVITY_KEY}`}
          dropdownWidth={300}
          {...directClickFocusProps}
        />
      );
    }
    default: {
      if (!field.property) {
        throw new Error(`Property is missing for field ${field.name} in TabularFieldFactory`);
      }

      const Component = propertyTypeToComponentMap[field.property.type] as ComponentType<
        ComponentProps | CustomFieldCellProps
      >;
      const hasDirectFocus = DIRECT_FOCUS_FIELD_TYPES.includes(field.property.type);

      return Component ? (
        <Component
          className={css.singleLineInput}
          title={title}
          formattedTitle={formattedFieldTitle}
          variant="table"
          property={field.property}
          value={value}
          onChange={onChange}
          subtemplate={subtemplate}
          portalRef={portalRef}
          roleFormKey={roleFormKey}
          dropdownWidth={TABULAR_FIELD_DROPDOWN_MIN_WIDTH}
          disabled={disabled}
          error={error}
          inputRef={inputRef as RefObject<any>}
          {...(hasDirectFocus ? directClickFocusProps : interactiveFocusProps)}
        />
      ) : null;
    }
  }
};
